import { ReactNode } from 'react';
import { Box, Menu } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

/**
 * Admin curation control for a recipe's two categorization axes:
 * category (taxonomy: what the dish IS) above the divider, and slots
 * (where the planner may SERVE it) below. Shared by the seed queue and
 * the catalog browser/detail pages so the verdict UI is identical
 * before and after approval.
 */

export type RecipeSlotsValue = {
  breakfast?: boolean;
  lunch?: boolean;
  dinner?: boolean;
};

// No "lunch": it was never a course - lunch-ness lives in slots (Serves)
export const CATEGORY_CHOICES: { value: string; label: string }[] = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'main_dish', label: 'Main dish' },
  { value: 'soup', label: 'Soup' },
  { value: 'salad', label: 'Salad' },
  { value: 'side_dish', label: 'Side dish' },
  { value: 'sauce', label: 'Sauce' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'bread', label: 'Bread' },
  { value: 'snack', label: 'Snack' },
  { value: 'beverage', label: 'Beverage' },
];

export const categoryLabel = (category: string | undefined): string =>
  CATEGORY_CHOICES.find((c) => c.value === (category ?? 'main_dish'))?.label ??
  'Main dish';

// Mirrors the backend's deriveSlotsFromCategory, so docs written before
// slots existed still display their effective serving slots
export const deriveSlots = (category: string | undefined): RecipeSlotsValue => {
  if (category === 'breakfast') return { breakfast: true };
  // Legacy tolerance: pre-removal docs may still say "lunch"
  if (category === 'lunch') return { lunch: true };
  if (
    ['side_dish', 'sauce', 'dessert', 'bread', 'snack', 'beverage'].includes(
      category ?? ''
    )
  ) {
    return {};
  }
  return { dinner: true };
};

const SLOT_CHOICES: { key: keyof RecipeSlotsValue; label: string }[] = [
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'dinner', label: 'Dinner' },
];

export const CategorizationMenu = ({
  category,
  slots,
  onChangeCategory,
  onChangeSlots,
  target,
}: {
  category?: string;
  slots?: RecipeSlotsValue;
  onChangeCategory: (category: string) => void;
  onChangeSlots: (slots: RecipeSlotsValue) => void;
  /** Menu.Target child; must forward a ref (Badge, Button, ActionIcon). */
  target: ReactNode;
}) => {
  const effective = slots ?? deriveSlots(category);
  return (
    <Menu withinPortal position="bottom-end" shadow="md">
      <Menu.Target>{target}</Menu.Target>
      {/* Portaled dropdowns still bubble clicks through the React tree, so
          the dropdown stops propagation or a parent card's click fires */}
      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
        <Menu.Label>Category</Menu.Label>
        {CATEGORY_CHOICES.map((choice) => {
          const current = choice.value === (category ?? 'main_dish');
          return (
            <Menu.Item
              key={choice.value}
              disabled={current}
              leftSection={current ? <IconCheck size={14} /> : <Box w={14} />}
              onClick={() => onChangeCategory(choice.value)}
            >
              {choice.label}
            </Menu.Item>
          );
        })}
        <Menu.Divider />
        <Menu.Label>Serves</Menu.Label>
        {SLOT_CHOICES.map(({ key, label }) => {
          const on = !!effective[key];
          return (
            <Menu.Item
              key={key}
              closeMenuOnClick={false}
              leftSection={on ? <IconCheck size={14} /> : <Box w={14} />}
              onClick={() => onChangeSlots({ ...effective, [key]: !on })}
            >
              {label}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};
